import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MapPin, Navigation } from 'lucide-react';
import TopBar from '../components/common/TopBar';
import BottomNav from '../components/common/BottomNav';
import LoadingSpinner from '../components/common/LoadingSpinner';
import { useLocation } from '../contexts/LocationContext';
import { kioskService, beachService } from '../services/api';
import { toast } from 'sonner';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix default marker icon (Leaflet + webpack/vite issue)
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

// Custom icon for open kiosks
const openIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-green.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

// Custom icon for closed kiosks
const closedIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-red.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

const MapView = () => {
  const navigate = useNavigate();
  const { location, requestLocation, hasLocation } = useLocation();
  const [kiosks, setKiosks] = useState([]);
  const [beaches, setBeaches] = useState([]);
  const [loading, setLoading] = useState(true);

  // 2026-06-29: envolvido com useCallback pra satisfazer ESLint react-hooks/exhaustive-deps
  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [kiosksData, beachesData] = await Promise.all([
        location
          ? kioskService.getNearby(location.latitude, location.longitude, 5000)
          : kioskService.list(null),  // no filter = show all (open + closed)
        beachService.list(),
      ]);
      setKiosks(kiosksData);
      setBeaches(beachesData);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  // 2026-06-29: location é dependência — recarrega quando muda
  }, [location]);

  // 2026-06-29: loadData agora é useCallback — pode ir no dep array
  useEffect(() => {
    loadData();
  }, [loadData]);

  // Default center (Jurerê, Florianópolis)
  const defaultCenter = [-27.4383, -48.4936];
  const mapCenter = location
    ? [location.latitude, location.longitude]
    : defaultCenter;

  return (
    <div className="min-h-screen bg-background pb-20" data-testid="map-page">
      <TopBar title="Mapa" />

      <div className="max-w-md mx-auto">
        {/* Leaflet Map */}
        <div className="relative h-80 z-0">
          <MapContainer
            center={mapCenter}
            zoom={14}
            scrollWheelZoom={true}
            className="h-full w-full"
            style={{ height: '100%', width: '100%' }}
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            {/* User location marker */}
            {location && (
              <Marker position={[location.latitude, location.longitude]}>
                <Popup>Você está aqui</Popup>
              </Marker>
            )}
            {/* Kiosk markers */}
            {kiosks
              .filter((k) => k.location && k.location.coordinates && k.location.coordinates.length === 2)
              .map((k) => {
                const [lng, lat] = k.location.coordinates;
                return (
                  <Marker
                    key={k.id}
                    position={[lat, lng]}
                    icon={k.effective_is_open ? openIcon : closedIcon}
                    eventHandlers={{
                      click: () => navigate(`/kiosk/${k.id}`),
                    }}
                  >
                    <Popup>
                      <div className="text-center">
                        <strong className="text-sm">{k.display_name}</strong>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          {k.effective_is_open ? '✅ Aberto' : '🔴 Fechado'}
                        </p>
                        <button
                          onClick={() => navigate(`/kiosk/${k.id}`)}
                          className="mt-1 px-2 py-1 bg-primary text-white text-xs rounded-full font-semibold"
                        >
                          Ver cardápio
                        </button>
                      </div>
                    </Popup>
                  </Marker>
                );
              })}
          </MapContainer>
        </div>

        <div className="p-4 space-y-4">
          {/* Location button (if no location) */}
          {!hasLocation && (
            <button
              data-testid="enable-location-map"
              onClick={requestLocation}
              className="w-full px-4 py-3 bg-primary text-primary-foreground rounded-xl font-semibold flex items-center justify-center gap-2 active:scale-95"
            >
              <Navigation className="w-5 h-5" />
              Ativar localização para ver quiosques próximos
            </button>
          )}

          {/* Beaches selector */}
          {beaches.length > 0 && (
            <div>
              <h2 className="font-['Outfit'] font-bold text-lg mb-3">Praias</h2>
              <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide" style={{ scrollbarWidth: 'none' }}>
                {beaches.map((beach) => (
                  <button
                    key={beach.id}
                    data-testid={`beach-${beach.id}`}
                    className="px-4 py-2 bg-white border border-border rounded-full whitespace-nowrap font-semibold text-sm hover:border-primary transition-colors"
                  >
                    🏖️ {beach.name}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Kiosks list */}
          <div>
            <h2 className="font-['Outfit'] font-bold text-lg mb-3">
              {hasLocation ? 'Estabelecimentos Próximos' : 'Estabelecimentos Disponíveis'}
            </h2>
            {loading ? (
              <LoadingSpinner />
            ) : kiosks.length === 0 ? (
              <div className="text-center py-8 bg-white rounded-xl border border-border">
                <p className="text-muted-foreground">Nenhum estabelecimento encontrado</p>
              </div>
            ) : (
              <div className="space-y-3">
                {kiosks.map((kiosk) => (
                  <button
                    key={kiosk.id}
                    data-testid={`map-kiosk-${kiosk.id}`}
                    onClick={() => navigate(`/kiosk/${kiosk.id}`)}
                    className="w-full bg-white rounded-xl shadow-sm border border-border p-4 text-left hover:shadow-md transition-shadow active:scale-[0.98] flex items-center gap-3"
                  >
                    <div className="w-12 h-12 bg-gradient-to-br from-primary/20 to-secondary/20 rounded-lg flex items-center justify-center flex-shrink-0">
                      <span className="text-2xl">🏖️</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-foreground truncate">
                        {kiosk.display_name}
                      </h3>
                      <p className="text-xs text-muted-foreground line-clamp-1">
                        {kiosk.description}
                      </p>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-xs text-secondary flex items-center gap-1">
                          <MapPin className="w-3 h-3" />
                          Raio: {kiosk.service_radius}m
                        </span>
                        <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${kiosk.effective_is_open ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                          {kiosk.effective_is_open ? 'Aberto' : 'Fechado'}
                        </span>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <BottomNav />
    </div>
  );
};

export default MapView;
